/**
 * Рапорт на виплату винагороди за особливості проходження служби.
 *
 * Документ складається з двох переліків людей, і обидва виводяться з таблиці:
 * кому виплатити — усі, хто був на службі, з періодами; кому не виплачувати —
 * ті, у кого чинне дисциплінарне стягнення. Поіменно діловод обирає лише
 * других: стягнення в таблиці не позначають, це не подія дня.
 *
 * Розрив періоду теж живе в таблиці. День, у якому стоїть позначка з
 * `awayReason` («лр» — хвороба, «в» — відпустка), випадає зі служби, а причина
 * йде в дужки: «за період з 01.05 по 18.05, за період з 20.05 по 31.05
 * (хвороба з 19.05.2026 по 19.05.2026)». Нова причина заводиться в довіднику
 * позначок і тут нічого не міняє.
 *
 * Модуль чистий — ті самі рядки рахує і сервер, і браузер.
 */
import { dayMonth, rangeDates } from './dates.js';
import { cellKey } from './marks.js';
import { cmpUk, nameForms, rankForm } from './people.js';
import { positionOf, unitNameOf, unitPhrase } from './documents.js';
/** «2026-05-19» -> «19.05.2026»: у рапорті дати пишуться повністю. */
export const dmy = (d) => `${d.slice(8)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;
/** «2026-05-01» -> «01 травня»: день із нулем попереду, місяць у родовому. */
const dayOfMonth = (d, locale) => `${d.slice(8)} ${dayMonth(d, locale).replace(/^\d+\s+/, '')}`;
/** Причини відсутності за кодами позначок: «lr» -> «хвороба». */
export const awayReasons = (types) => new Map(types.filter(t => (t.awayReason ?? '').trim()).map(t => [t.code, t.awayReason.trim()]));
/**
 * Дні, у які людини на службі не було. Позначка стоїть по клітинках прийомів
 * їжі, а зі служби випадає весь день: хворий не ходить на заняття зранку й
 * повертається до вечері. Тому досить однієї клітинки; якщо їх кілька з
 * різними причинами, лишається перша за порядком прийомів їжі.
 *
 * Дивимось усі три шари. Відсутність тепер ставлять у таблиці відпусток, але
 * в старих місяцях вона стоїть у харчуванні чи серед нарядів, — а для рапорту
 * це той самий день поза службою: причина живе в самій позначці, не в шарі.
 */
export function awayDaysOf(index, personId, dates, mealCount, reasons) {
    const out = new Map();
    const marks = index.person(personId);
    if (!marks || !reasons.size)
        return out;
    for (const d of dates) {
        for (let m = 0; m < mealCount; m++) {
            const key = cellKey(d, m);
            const reason = reasons.get(marks.away.get(key) ?? '') ??
                reasons.get(marks.main.get(key) ?? '') ??
                reasons.get(marks.duty.get(key) ?? '');
            if (reason) {
                out.set(d, reason);
                break;
            }
        }
    }
    return out;
}
/** Суцільні відрізки дат, у які людина була на службі. */
export function servedSpans(dates, away) {
    const out = [];
    dates.forEach((d, i) => {
        if (away.has(d))
            return;
        const last = out[out.length - 1];
        if (last && last.to === dates[i - 1])
            last.to = d;
        else
            out.push({ from: d, to: d });
    });
    return out;
}
/** Суцільні відрізки відсутності; сусідні дні з різних причин не зливаються. */
export function awaySpans(dates, away) {
    const out = [];
    dates.forEach((d, i) => {
        const reason = away.get(d);
        if (!reason)
            return;
        const last = out[out.length - 1];
        if (last && last.reason === reason && last.to === dates[i - 1])
            last.to = d;
        else
            out.push({ from: d, to: d, reason });
    });
    return out;
}
/** «за період з 01.05.2026 по 18.05.2026» — по відрізку на кожен. */
const servedPhrase = (spans) => spans.map(s => `за період з ${dmy(s.from)} по ${dmy(s.to)}`).join(', ');
/**
 * «хвороба з 28.08.2026 по 28.08.2026» — межі проміжку названі завжди, навіть
 * коли він одноденний: у рапорті день хвороби читається так само, як тиждень.
 */
const spanPhrase = (s) => `${s.reason} з ${dmy(s.from)} по ${dmy(s.to)}`;
/**
 * «(відпустка з 21.07.2026 по 09.08.2026, та хвороба з 28.08.2026 по 28.08.2026)»
 *
 * Усі перерви — в одних дужках і одним переліком. Останню приєднуємо через
 * «та»: так це читається реченням, а не переліком у таблиці.
 */
const awayPhrase = (gaps) => {
    const all = gaps.map(spanPhrase);
    if (!all.length)
        return '';
    const last = all.pop();
    return ` (${all.length ? `${all.join(', ')}, та ${last}` : last})`;
};
/**
 * «командиру групи» + «С-21 групи» = «командиру С-21 групи».
 *
 * Назва підрозділу вже містить слово, яким закінчується посада, і другий раз
 * воно в рядку зайве. Командира відділення це не зачіпає: у «командиру
 * І відділення С-25 групи» слова різні й обидва лишаються на місці.
 */
const dropRepeatedWord = (role, unit) => {
    const tail = unit.trim().split(/\s+/).pop() ?? '';
    if (!tail)
        return role;
    const cut = new RegExp(`\\s+${tail.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'iu');
    return role.replace(cut, '');
};
/**
 * «солдату КУЛІБАБІ Данилу Руслановичу (3843104196), курсанту С-25 групи
 * С2 курсу» — спільний початок обох переліків.
 */
function personPhrase(ctx, person) {
    const rank = rankForm(ctx.ranks, person.rankCode, 'dat');
    const name = nameForms(person).dat;
    const unit = unitPhrase(ctx, person);
    const role = dropRepeatedWord(positionOf(ctx.positions, person.positionCode)?.nameDat ?? '', unit);
    const where = `${unit}${ctx.course}`.trim();
    const code = person.taxCode ? ` (${person.taxCode})` : '';
    return `${rank} ${name}${code}, ${[role, where].filter(Boolean).join(' ')}`.replace(/\s+/g, ' ').trim();
}
/**
 * Місце в переліку задає посада, а не звання: рапорт читають ті, хто його
 * підписує, і починається він з керівництва курсу.
 *
 *   0 — старшина курсу
 *   1 — командири груп
 *   2 — командири відділень
 *   3 — решта складу
 *
 * Командир відділення пізнається за кодом посади `sqN`, як і в рештi програми
 * (`splitSquadsByGroup`), тож номер відділення береться звідти ж.
 */
const SQUAD_LEAD = /^sq(\d+)$/;
const LEVEL_REST = 3;
const levelOf = (code) => {
    if (code === 'master')
        return 0;
    if (code === 'platoon')
        return 1;
    return SQUAD_LEAD.test(code ?? '') ? 2 : LEVEL_REST;
};
const squadNo = (code) => Number(SQUAD_LEAD.exec(code ?? '')?.[1] ?? 0);
/**
 * Назви груп порівнюємо з числами: «С-2» має стояти перед «С-10», а не після.
 * Азбучне `cmpUk` тут не годиться — воно свідомо пропускає цифри, тож усі
 * групи виходили однаковими й порядок падав на прізвище.
 */
const cmpUnit = (a, b) => a.localeCompare(b, 'uk', { numeric: true });
/**
 * Керівництво — за групами (С-21, С-22, С-23), а в групі І відділення перед ІІ.
 * Решта складу йде суцільним списком за абеткою, як у затвердженому зразку:
 * там курсанти всіх груп ідуть упереміш.
 */
function byPostThenName(ctx) {
    const unitOf = (p) => unitNameOf(ctx.units, p.unitId);
    return (a, b) => {
        const levels = levelOf(a.positionCode) - levelOf(b.positionCode);
        if (levels)
            return levels;
        if (levelOf(a.positionCode) === LEVEL_REST)
            return cmpUk(a.lastName, b.lastName);
        return (cmpUnit(unitOf(a), unitOf(b)) ||
            squadNo(a.positionCode) - squadNo(b.positionCode) ||
            cmpUk(a.lastName, b.lastName));
    };
}
/**
 * Обидва переліки рапорту за період.
 *
 * До першого потрапляє кожен, хто був на службі хоч день і не має стягнення;
 * хто був відсутній увесь місяць, не потрапляє нікуди — виплачувати нема за
 * що, а до тих, кому «не виплачувати», його вписувати не можна: підстава там
 * інша. Такі люди повертаються окремо, щоб діловод їх побачив, а не шукав.
 */
export function bonusPlan(ctx, from, to, penalized = []) {
    const dates = rangeDates(from, to);
    const reasons = awayReasons(ctx.markTypes);
    const denySet = new Set(penalized);
    const order = byPostThenName(ctx);
    const rowOf = (person, withPeriods) => {
        const away = awayDaysOf(ctx.index, person.id, dates, ctx.meals.length, reasons);
        const served = servedSpans(dates, away);
        const gaps = awaySpans(dates, away);
        const periods = withPeriods && served.length ? `, ${servedPhrase(served)}${awayPhrase(gaps)}` : '';
        return { person, served, away: gaps, text: `${personPhrase(ctx, person)}${periods}` };
    };
    const people = ctx.people.filter(p => p.active).sort(order);
    const plan = { paid: [], denied: [], absent: [] };
    for (const person of people) {
        if (denySet.has(person.id)) {
            plan.denied.push(rowOf(person, false));
            continue;
        }
        const row = rowOf(person, true);
        (row.served.length ? plan.paid : plan.absent).push(row);
    }
    return plan;
}
/** Перелік готовими абзацами: крапка з комою між рядками, крапка в кінці. */
export const bonusLines = (rows) => rows.map((r, i) => `${r.text}${i === rows.length - 1 ? '.' : ';'}`);
/** Значення, які рапорт підставляє в бланк поза переліками. */
export function bonusValues(ctx, from, to, amount) {
    const month = dayMonth(from, ctx.locale).replace(/^\d+\s+/, '');
    const nominative = new Intl.DateTimeFormat(ctx.locale === 'uk' ? 'uk-UA' : ctx.locale, {
        month: 'long',
    }).format(new Date(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, 1));
    return {
        AMOUNT: amount,
        // «за травень 2026» — місяць у знахідному, який в українській збігається
        // з називним; рік беремо за початком періоду, бо рапорт місячний.
        MONTH_YEAR: `${nominative} ${from.slice(0, 4)}`,
        MONTH: month,
        // «з 01 травня по 31 травня 2026 року» — так період названий у пункті 2.
        PERIOD_PHRASE: `з ${dayOfMonth(from, ctx.locale)} по ${dayOfMonth(to, ctx.locale)} ${to.slice(0, 4)} року`,
    };
}
//# sourceMappingURL=bonus-report.js.map